import { apiRequest } from './client';
import type { LaunchResult, LauncherItem, LoginResult, PasswordTokenInfo } from './types';

export function login(identifier: string, password: string): Promise<LoginResult> {
  return apiRequest('/auth/login', { method: 'POST', body: { identifier, password } });
}

export function loginWithNfc(cardId: string): Promise<LoginResult> {
  return apiRequest('/auth/nfc', { method: 'POST', body: { cardId } });
}

export function logout(): Promise<void> {
  return apiRequest('/auth/logout', { method: 'POST' });
}

/** unified-access.md section 3.1 (KAN-50): reports clicks and key presses. */
export function recordActivity(): Promise<void> {
  return apiRequest('/auth/activity', { method: 'POST' });
}

export function me(): Promise<LoginResult> {
  return apiRequest('/auth/me');
}

/**
 * The API's `changePasswordSchema` requires `confirmPassword` alongside
 * `newPassword` (server-side match check, same as `setPassword` below) —
 * not only client-side validation.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiRequest('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword, confirmPassword: newPassword },
  });
}

export function forgotPassword(identifier: string): Promise<void> {
  return apiRequest('/auth/forgot-password', { method: 'POST', body: { identifier } });
}

export function getPasswordToken(token: string): Promise<PasswordTokenInfo> {
  return apiRequest(`/auth/password-token/${encodeURIComponent(token)}`);
}

export function setPassword(token: string, newPassword: string): Promise<void> {
  return apiRequest('/auth/set-password', {
    method: 'POST',
    body: { token, newPassword, confirmPassword: newPassword },
  });
}

export function selectBranch(branchId: string): Promise<void> {
  return apiRequest('/session/branch', { method: 'POST', body: { branchId } });
}

export function getLauncher(): Promise<LauncherItem[]> {
  return apiRequest('/launcher');
}

export function launch(platform: string): Promise<LaunchResult> {
  return apiRequest('/launch', { method: 'POST', body: { platform } });
}

/**
 * unified-access.md §3 step 5: resolve a platform's absolute return URL
 * (from `?returnTo=`) into a vetted launch URL. The server rejects any
 * origin that is not an active platform's `webUrl` — callers must navigate
 * only to the URL this resolves to, never to the raw `returnTo` string.
 */
export function launchReturn(returnTo: string): Promise<LaunchResult> {
  return apiRequest('/launch/return', { method: 'POST', body: { returnTo } });
}
